/**
 * CostBreakdownChart Component
 *
 * Displays a horizontal bar chart showing cost breakdown by category.
 * Shows the relative contribution of each cost category to total costs.
 *
 * Part of US-P7-001 Step 5: Cost Summary Dashboard
 */

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'

/**
 * Category color mapping
 * Using subtle, professional colors from Odin style guide
 */
const CATEGORY_COLORS = {
  labour: '#6366f1',      // Indigo
  rent: '#8b5cf6',        // Purple
  utilities: '#ec4899',   // Pink
  energy: '#f59e0b',      // Amber
  insurance: '#f97316',   // Orange
  equipment: '#14b8a6',   // Teal
  materials: '#10b981',   // Green
  other: '#64748b'        // Slate
}

/**
 * Get color for a category
 *
 * @param {string} category - Category name
 * @returns {string} Hex color code
 */
function getCategoryColor(category) {
  const normalizedCategory = category.toLowerCase().replace(/\s+/g, '_')
  return CATEGORY_COLORS[normalizedCategory] || CATEGORY_COLORS.other
}

/**
 * Format currency for display
 *
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value >= 1000) {
    return `£${(value / 1000).toFixed(1)}k`
  }
  return `£${value.toFixed(0)}`
}

/**
 * Custom tooltip component
 */
function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null

  const data = payload[0].payload
  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-neutral-900">{data.category}</p>
      <p className="text-sm text-neutral-600">
        {new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: 'GBP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(data.amount)}
      </p>
    </div>
  )
}

/**
 * CostBreakdownChart Component
 *
 * @param {Object} props
 * @param {Object} props.breakdown - Cost breakdown by category { category: amount }
 * @param {boolean} [props.isLoading] - Loading state
 * @param {string} [props.className] - Additional CSS classes
 */
export default function CostBreakdownChart({
  breakdown,
  isLoading = false,
  className = ''
}) {
  // Transform breakdown object to chart data array
  const chartData = breakdown
    ? Object.entries(breakdown)
        .map(([category, amount]) => ({
          category: category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          amount,
          color: getCategoryColor(category)
        }))
        .sort((a, b) => b.amount - a.amount) // Sort by amount descending
    : []

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Category Breakdown</CardTitle>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-neutral-500">
            <p>No cost data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                horizontal={false}
              />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={formatCurrency}
              />
              <YAxis
                type="category"
                dataKey="category"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                width={80}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
              <Bar
                dataKey="amount"
                radius={[0, 4, 4, 0]}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * CostBreakdownChartSkeleton - Loading state for CostBreakdownChart
 *
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 */
export function CostBreakdownChartSkeleton({ className = '' }) {
  return (
    <Card className={className}>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      </CardContent>
    </Card>
  )
}
